import { useState, useEffect, useCallback } from 'react';

interface UsePersistedFiltersOptions<T> {
  storageKey: string;
  defaultFilters: T;
}

/**
 * Hook to persist filter state in sessionStorage
 * This allows filters to be preserved when navigating away and back to a page
 */
export function usePersistedFilters<T extends Record<string, any>>({
  storageKey,
  defaultFilters,
}: UsePersistedFiltersOptions<T>) {
  // Initialize state from sessionStorage or defaults
  const [filters, setFilters] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all keys exist
        return { ...defaultFilters, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load filters from sessionStorage:', error);
    }
    return defaultFilters;
  });

  // Save to sessionStorage whenever filters change
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (error) {
      console.error('Failed to save filters to sessionStorage:', error);
    }
  }, [filters, storageKey]);

  // Update a single filter
  const updateFilter = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Update multiple filters at once
  const updateFilters = useCallback((updates: Partial<T>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  // Reset filters to defaults
  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  // Clear filters from storage (useful for cleanup)
  const clearStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to clear filters from sessionStorage:', error);
    }
  }, [storageKey]);

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    clearStorage,
  };
}
